import { Injectable } from '@angular/core';

import { Observable, timer, switchMap, takeWhile, map } from 'rxjs';

import { ApiService } from '../http/api.service';



export const JOURNAL_TRANSCRIBING_PLACEHOLDER = 'Transcribing...';



export type JournalEntryType = 'TEXT' | 'AUDIO' | string;

export type JournalEntryStatus = 'DRAFT' | 'PUBLISHED' | string;



export interface JournalEntry {

  id: string;

  title?: string | null;

  type: JournalEntryType;

  content: string;

  status?: JournalEntryStatus;

  created_on?: string;

  updated_on?: string;

  mood?: string | null;

  tags?: string[] | string | null;

  is_private?: boolean;

  file_id?: string | null;

}



export interface JournalDictationItem {

  id: string;

  title: string;

  type: 'AUDIO' | string;

  content: string;

  created_on: string;

}



export interface JournalDictationCreateResponse {

  journalId: string;

  jobId: string;

}



export interface JournalTextDictationPayload {

  content: string;

  title?: string;

  mood?: string;

  tags?: string[] | string;

  is_private?: boolean;

  isPrivate?: boolean;

}



export interface JournalEntryUpdatePayload {

  content?: string;

  title?: string;

  mood?: string;

  tags?: string[] | string;

  is_private?: boolean;

  isPrivate?: boolean;

}



export interface JournalTextDictationResponse {
  journalId: string;
}

export interface JournalListOptions {
  limit?: number;
  last_created_on?: string;
  page?: number;
}

export interface JournalListResponse<T = JournalEntry> {
  items: T[];
  total: number;
  hasMore: boolean;
}



@Injectable({

  providedIn: 'root'

})

export class JournalDictationsService {

  private readonly DICTATIONS_PATH = 'journal/dictations';

  private readonly ENTRIES_PATH = 'journal/entries';



  constructor(private api: ApiService) {}



  /** Save a text journal entry (typed or client-side STT). */
  createTextDictation(
    payload: JournalTextDictationPayload
  ): Observable<JournalTextDictationResponse> {
    const body = {
      content: payload.content,
      title: payload.title,
      mood: payload.mood,
      tags: payload.tags,
      isPrivate: payload.isPrivate ?? payload.is_private ?? true
    };

    return this.api.post<any>(this.ENTRIES_PATH, body).pipe(
      map((response: any) => {
        const data = response?.data ?? response;
        return {
          journalId: data?.journalId ?? data?.id
        } as JournalTextDictationResponse;
      })
    );
  }



  /** Enqueue dictation transcription by uploading audio. */

  enqueueDictation(

    file: Blob,

    meta?: {

      title?: string;

      mood?: string;

      tags?: string[] | string;

      is_private?: boolean;

      isPrivate?: boolean;

    }

  ): Observable<JournalDictationCreateResponse> {

    const formData = new FormData();

    const mime = (file.type ?? '').toLowerCase();

    const ext =

      mime.includes('mpeg') || mime.includes('mp3') ? 'mp3' :

      mime.includes('wav') ? 'wav' :

      mime.includes('ogg') ? 'ogg' :

      mime.includes('mp4') ? 'mp4' :

      mime.includes('webm') ? 'webm' :

      'audio';



    console.log('enqueueDictation: uploading audio', { mime: file.type, ext, size: file.size });

    formData.append('file', file, `dictation_${Date.now()}.${ext}`);



    if (meta?.title) formData.append('title', meta.title);

    if (meta?.mood) formData.append('mood', meta.mood);



    if (meta?.tags) {

      if (Array.isArray(meta.tags)) {

        formData.append('tags', JSON.stringify(meta.tags));

      } else {

        formData.append('tags', meta.tags);

      }

    }



    const isPrivate = meta?.is_private ?? meta?.isPrivate;

    if (typeof isPrivate === 'boolean') {

      formData.append('is_private', String(isPrivate));

    }



    return this.api

      .uploadFormData(this.DICTATIONS_PATH, formData)

      .pipe(

        map((response: any) => {

          const payload = response?.data ?? response;

          return {

            journalId: payload?.journalId,

            jobId: payload?.jobId

          } as JournalDictationCreateResponse;

        })

      );

  }



  /** Full journal timeline — text, audio, all statuses (newest first). */
  listEntries(options: JournalListOptions = {}): Observable<JournalListResponse> {
    const params: Record<string, string | number> = {
      limit: options.limit ?? 20
    };

    if (options.last_created_on) {
      params['last_created_on'] = options.last_created_on;
    }

    if (options.page != null) {
      params['page'] = options.page;
    }

    return this.api.get<any>(this.ENTRIES_PATH, params).pipe(
      map((response: any) => {
        const payload = response?.data ?? response;
        return {
          items: (payload?.items ?? []) as JournalEntry[],
          total: payload?.total ?? 0,
          hasMore: payload?.hasMore ?? false
        };
      })
    );
  }



  /** Update an existing journal entry (text content / title). */
  updateEntry(
    entryId: string,
    payload: JournalEntryUpdatePayload
  ): Observable<JournalEntry> {
    const body = {
      content: payload.content,
      title: payload.title,
      mood: payload.mood,
      tags: payload.tags,
      isPrivate: payload.isPrivate ?? payload.is_private
    };

    return this.api.put<any>(`${this.ENTRIES_PATH}/${entryId}`, body).pipe(
      map((response: any) => (response?.data ?? response) as JournalEntry)
    );
  }



  /** List only published audio dictations with finished transcripts (newest first). */
  listPublishedDictations(options: JournalListOptions = {}): Observable<JournalListResponse<JournalDictationItem>> {
    const params: Record<string, string | number> = {
      limit: options.limit ?? 20
    };

    if (options.last_created_on) {
      params['last_created_on'] = options.last_created_on;
    }

    if (options.page != null) {
      params['page'] = options.page;
    }

    return this.api.get<any>(this.DICTATIONS_PATH, params).pipe(
      map((response: any) => {
        const payload = response?.data ?? response;
        return {
          items: payload?.items ?? [],
          total: payload?.total ?? 0,
          hasMore: payload?.hasMore ?? false
        };
      })
    );
  }



  isTranscribing(entry: Pick<JournalEntry, 'content' | 'status' | 'type'> | null | undefined): boolean {
    if (!entry) return false;
    const content = (entry.content ?? '').trim();
    if (content === JOURNAL_TRANSCRIBING_PLACEHOLDER) {
      return true;
    }
    return entry.type === 'AUDIO' && entry.status === 'DRAFT' && !content;
  }



  /**

   * Poll the full journal list until the entry is no longer transcribing.

   * Uses GET /journal/entries so DRAFT / "Transcribing..." rows are visible.

   */

  pollUntilTranscribed(
    journalId: string,
    limit: number = 50
  ): Observable<JournalEntry | null> {
    return timer(0, 3000).pipe(
      switchMap(() => this.listEntries({ limit })),
      map(({ items }) => items.find(i => i.id === journalId) ?? null),

      takeWhile((found, index) => {

        if (!found) {

          return index < 40;

        }

        return this.isTranscribing(found) && index < 40;

      }, true)

    );

  }



  /** @deprecated Prefer pollUntilTranscribed — dictations list hides in-progress items. */

  pollUntilPublished(
    journalId: string,
    limit: number = 20
  ): Observable<JournalDictationItem | null> {
    return timer(0, 3000).pipe(
      switchMap(() => this.listPublishedDictations({ limit })),
      map(({ items }) => items.find(i => i.id === journalId) ?? null),

      takeWhile((found, index) => found === null && index < 40, true)

    );

  }

}


